import useLightenBackground from 'embed_v2/hooks/useLightenBackground'
import { Fragment } from 'preact'
import { useContext, useEffect, useRef } from 'preact/hooks'
import { getStylesFn } from 'core/helpers/styles'
import { ElementButtonOptions, type Icon as IconType } from 'core/types/element/element_button'
import generateOptionsPropTypes from 'PropsSpec'
import WidgetContext from '../Context'
import Icon from './common/Icon'

const WRAPPER_STYLES = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  boxSizing: 'inherit'
}

const extractStyles = getStylesFn(
  [
    'borderTop',
    'borderRight',
    'borderBottom',
    'borderLeft',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderBottomRightRadius',
    'borderBottomLeftRadius',
    'opacity',
    'backgroundColor',
    'paddingTop',
    'paddingRight',
    'paddingBottom',
    'paddingLeft'
  ],
  {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'inherit',
    cursor: 'pointer',
    transition: 'box-shadow 150ms',
    boxShadow: 'none',
    padding: '12px 10px'
  }
)

const extractInnerStyles = getStylesFn(
  [
    'color',
    'textAlign',
    'lineHeight',
    'textTransform',
    'textDecoration',
    'letterSpacing',
    'textShadow',
    'fontFamily',
    'fontSize',
    'fontWeight'
  ],
  {
    width: '100%',
    overflow: 'hidden',
    padding: '0 10px',
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    boxSizing: 'inherit'
  }
)

const extractInnerSubStyles = getStylesFn(
  [
    'marginTop',
    'color',
    'textAlign',
    'lineHeight',
    'textTransform',
    'textDecoration',
    'letterSpacing',
    'textShadow',
    'fontFamily',
    'fontSize',
    'fontWeight'
  ],
  {
    width: '100%',
    overflow: 'hidden',
    padding: '0 10px',
    wordWrap: 'break-word',
    whiteSpace: 'normal',
    boxSizing: 'inherit'
  }
)

const TEXT_WRAPPER_STYLES = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center'
}

const TURNSTILE_WRAPPER_STYLES = {
  display: 'flex',
  justifyContent: 'center',
  padding: '10px 0',
  boxSizing: 'inherit'
}

const iconOptions = (icon: IconType) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: `rgba(${icon.color})`,
  fontSize: icon.size,
  minWidth: 'auto'
})

const actions = {
  goToUrl: 'go_to_url',
  nextStep: 'next_step'
}

type Props = {
  options: ElementButtonOptions
}

const Button = ({ options }: Props) => {
  const {
    nextStep,
    isSubmitting,
    canNextStep,
    unsafeTagRegistrant,
    version,
    cloudflareTurnstileRef,
    cloudflareTurnstileInteractive
  } = useContext(WidgetContext)
  const { left_icon: leftIcon, right_icon: rightIcon } = options
  const node = useLightenBackground<HTMLButtonElement>()
  const autoHeightRef = useRef(false)

  useEffect(() => {
    const clickedEnterEventListener = (event: KeyboardEvent) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault()
        nextStep()
      }
    }

    if (options.action === actions.nextStep && canNextStep) {
      document.addEventListener('keydown', clickedEnterEventListener)

      return () => {
        document.removeEventListener('keydown', clickedEnterEventListener)
      }
    }
  }, [nextStep, canNextStep])

  const onClick = () => {
    switch (options.action) {
      case actions.nextStep:
        if (canNextStep) {
          nextStep()
        }
        break
      case actions.goToUrl:
        if (options.action_go_to_url) {
          window.open(options.action_go_to_url, '_parent')
        }
        break
      case 'tag_registrant':
        if (options.action_tag?.length) {
          unsafeTagRegistrant(options.action_tag)
        }
        break
      default:
        break
    }
  }

  if (cloudflareTurnstileInteractive) {
    autoHeightRef.current = true
  }

  const autoHeight = autoHeightRef.current
  const wrapperStyles = autoHeight ? { ...WRAPPER_STYLES, height: 'auto' } : WRAPPER_STYLES
  const extractedStyles = extractStyles(options)
  const styles = autoHeight ? { ...extractedStyles, height: 'auto' } : extractedStyles
  const innerStyles = extractInnerStyles(options.text)
  const innerSubStyles = extractInnerSubStyles(options.subtext)

  return (
    <Fragment>
      {version.cloudflare_turnstile_enabled && options.action === actions.nextStep && (
        <div
          className="wf_cloudflare_turnstile wf_reset"
          ref={cloudflareTurnstileRef}
          style={TURNSTILE_WRAPPER_STYLES}
        />
      )}
      <div className="wf_button_wrapper wf_reset" style={wrapperStyles}>
        <button className="wf_button wf_reset" ref={node} onClick={onClick} style={styles} disabled={isSubmitting}>
          <div className="wf_reset wf_button_text_wrapper" style={TEXT_WRAPPER_STYLES}>
            {!!leftIcon?.code?.length && (
              <Icon className="wf_reset wf_button_left_icon" code={leftIcon.code} styles={iconOptions(leftIcon)} />
            )}
            <div className="wf_reset wf_button_text" style={innerStyles}>
              {options.button_text}
            </div>
            {!!rightIcon?.code?.length && (
              <Icon className="wf_reset wf_button_right_icon" code={rightIcon.code} styles={iconOptions(rightIcon)} />
            )}
          </div>
          {!!options.button_subtext?.length && (
            <div className="wf_reset wf_button_subtext" style={innerSubStyles}>
              {options.button_subtext}
            </div>
          )}
        </button>
      </div>
    </Fragment>
  )
}

Button.propTypes = {
  options: generateOptionsPropTypes('button')
}

export default Button
